// ====================================================================
// CLIENT FOLLOW-UP CRM - ADMIN DASHBOARD VIEW
// ====================================================================

import Chart from 'chart.js/auto';
import { getClients, getInteractions } from '../store.js';
import { escapeHtml, formatDate } from '../ui.js';

let monthlyClientsChart = null;
let monthlyInteractionsChart = null;
let statusDistributionChart = null;
let followupCompletionChart = null;

export async function renderDashboardView(container) {
  const clients = await getClients();
  const interactions = await getInteractions();

  const todayStr = new Date().toISOString().split('T')[0];

  // Calculate Metrics
  const totalClients = clients.length;
  const totalInteractions = interactions.length;
  const hotLeads = clients.filter(c => c.category === 'Hot').length;
  const closedClients = clients.filter(c => c.category === 'Closed').length;

  // Follow-up categorization
  const overdueFollowups = interactions.filter(i => i.next_followup_date && i.next_followup_date < todayStr && i.status !== 'Won' && i.status !== 'Lost' && i.status !== 'Closed');
  const todayFollowups = interactions.filter(i => i.next_followup_date === todayStr && i.status !== 'Won' && i.status !== 'Lost' && i.status !== 'Closed');
  const upcomingFollowups = interactions.filter(i => i.next_followup_date > todayStr && i.status !== 'Won' && i.status !== 'Lost' && i.status !== 'Closed');

  const recentInteractions = interactions.slice(0, 5);
  const recentlyAddedClients = [...clients].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 5);

  container.innerHTML = `
    <div class="page-header">
      <div>
        <h1 class="page-title">Executive Dashboard</h1>
        <p class="page-subtitle">Overview of client interactions, follow-ups, and sales metrics</p>
      </div>
      <a href="#clients" class="btn btn-primary">
        <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/></svg>
        Manage Clients
      </a>
    </div>

    <!-- KPI Metric Cards -->
    <div class="kpi-grid">
      <div class="kpi-card">
        <div class="kpi-header">
          <span class="kpi-title">Total Clients</span>
          <div class="kpi-icon">
            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/></svg>
          </div>
        </div>
        <div class="kpi-value">${totalClients}</div>
      </div>

      <div class="kpi-card">
        <div class="kpi-header">
          <span class="kpi-title">Total Interactions</span>
          <div class="kpi-icon">
            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/></svg>
          </div>
        </div>
        <div class="kpi-value">${totalInteractions}</div>
      </div>

      <div class="kpi-card today">
        <div class="kpi-header">
          <span class="kpi-title">Today's Follow-ups</span>
          <div class="kpi-icon">
            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
          </div>
        </div>
        <div class="kpi-value">${todayFollowups.length}</div>
      </div>

      <div class="kpi-card overdue">
        <div class="kpi-header">
          <span class="kpi-title">Overdue Follow-ups</span>
          <div class="kpi-icon">
            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/></svg>
          </div>
        </div>
        <div class="kpi-value" style="color: var(--color-overdue);">${overdueFollowups.length}</div>
      </div>

      <div class="kpi-card upcoming">
        <div class="kpi-header">
          <span class="kpi-title">Upcoming Follow-ups</span>
          <div class="kpi-icon">
            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
          </div>
        </div>
        <div class="kpi-value" style="color: var(--color-upcoming);">${upcomingFollowups.length}</div>
      </div>

      <div class="kpi-card">
        <div class="kpi-header">
          <span class="kpi-title">Hot Leads</span>
          <div class="kpi-icon" style="background-color: var(--cat-hot-bg); color: var(--cat-hot);">
            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z"/></svg>
          </div>
        </div>
        <div class="kpi-value">${hotLeads}</div>
      </div>

      <div class="kpi-card">
        <div class="kpi-header">
          <span class="kpi-title">Closed Clients</span>
          <div class="kpi-icon">
            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
          </div>
        </div>
        <div class="kpi-value">${closedClients}</div>
      </div>
    </div>

    <!-- Analytics Charts Row -->
    <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); gap: 1.5rem; margin-bottom: 1.75rem;">
      <div class="card">
        <div class="card-header">
          <h3 class="card-title">Monthly New Clients</h3>
        </div>
        <canvas id="chart-monthly-clients" height="200"></canvas>
      </div>

      <div class="card">
        <div class="card-header">
          <h3 class="card-title">Monthly Interactions</h3>
        </div>
        <canvas id="chart-monthly-interactions" height="200"></canvas>
      </div>

      <div class="card">
        <div class="card-header">
          <h3 class="card-title">Lead Status Distribution</h3>
        </div>
        <canvas id="chart-status-dist" height="200"></canvas>
      </div>

      <div class="card">
        <div class="card-header">
          <h3 class="card-title">Follow-up Completion Rate</h3>
        </div>
        <canvas id="chart-followup-completion" height="200"></canvas>
      </div>
    </div>

    <!-- Dashboard Alert Tables -->
    <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(400px, 1fr)); gap: 1.5rem;">
      <!-- Overdue Follow-ups -->
      <div class="card" style="border-top: 4px solid var(--color-overdue);">
        <div class="card-header">
          <h3 class="card-title" style="color: var(--color-overdue);">Overdue Follow-ups</h3>
          <span class="badge badge-overdue">${overdueFollowups.length} Priority</span>
        </div>
        <div class="table-responsive">
          <table class="table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Due Date</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              ${overdueFollowups.length === 0 ? '<tr><td colspan="4" style="text-align:center; color:var(--text-muted);">No overdue follow-ups! Great job 🎉</td></tr>' : 
                overdueFollowups.map(i => `
                  <tr>
                    <td><strong>${escapeHtml(i.client?.client_name || 'Client')}</strong><br><span style="font-size:0.75rem; color:var(--text-muted);">${escapeHtml(i.client?.mobile_number || '')}</span></td>
                    <td><span class="badge badge-overdue">${formatDate(i.next_followup_date)}</span></td>
                    <td><span class="badge badge-warm">${escapeHtml(i.status)}</span></td>
                    <td><a href="#profile?id=${i.client_id}" class="btn btn-secondary btn-sm">Log Interaction</a></td>
                  </tr>
                `).join('')
              }
            </tbody>
          </table>
        </div>
      </div>

      <!-- Today's Follow-ups -->
      <div class="card" style="border-top: 4px solid var(--color-today);">
        <div class="card-header">
          <h3 class="card-title" style="color: var(--color-today);">Today's Follow-ups</h3>
          <span class="badge badge-today">${todayFollowups.length} Due Today</span>
        </div>
        <div class="table-responsive">
          <table class="table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Status</th>
                <th>Notes</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              ${todayFollowups.length === 0 ? '<tr><td colspan="4" style="text-align:center; color:var(--text-muted);">No follow-ups scheduled for today.</td></tr>' :
                todayFollowups.map(i => `
                  <tr>
                    <td><strong>${escapeHtml(i.client?.client_name || 'Client')}</strong><br><span style="font-size:0.75rem; color:var(--text-muted);">${escapeHtml(i.client?.mobile_number || '')}</span></td>
                    <td><span class="badge badge-today">${escapeHtml(i.status)}</span></td>
                    <td style="max-width:180px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">${escapeHtml(i.notes || '-')}</td>
                    <td><a href="#profile?id=${i.client_id}" class="btn btn-primary btn-sm">Follow Up</a></td>
                  </tr>
                `).join('')
              }
            </tbody>
          </table>
        </div>
      </div>

      <!-- Recent Interactions -->
      <div class="card">
        <div class="card-header">
          <h3 class="card-title">Recent Activity</h3>
        </div>
        <div class="table-responsive">
          <table class="table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Date</th>
                <th>Status</th>
                <th>Logged By</th>
              </tr>
            </thead>
            <tbody>
              ${recentInteractions.map(i => `
                <tr>
                  <td><a href="#profile?id=${i.client_id}" style="color: var(--primary); text-decoration:none; font-weight:600;">${escapeHtml(i.client?.client_name || 'Client')}</a></td>
                  <td>${formatDate(i.interaction_date)}</td>
                  <td><span class="badge badge-warm">${escapeHtml(i.status)}</span></td>
                  <td>${escapeHtml(i.creator?.name || 'Staff')}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      </div>

      <!-- Recently Added Clients -->
      <div class="card">
        <div class="card-header">
          <h3 class="card-title">Recently Added Clients</h3>
        </div>
        <div class="table-responsive">
          <table class="table">
            <thead>
              <tr>
                <th>Client Name</th>
                <th>Company</th>
                <th>Mobile</th>
                <th>Category</th>
              </tr>
            </thead>
            <tbody>
              ${recentlyAddedClients.map(c => `
                <tr>
                  <td><a href="#profile?id=${c.id}" style="color: var(--text-primary); text-decoration:none; font-weight:600;">${escapeHtml(c.client_name)}</a></td>
                  <td>${escapeHtml(c.company || '-')}</td>
                  <td>${escapeHtml(c.mobile_number)}</td>
                  <td><span class="badge badge-${(c.category || 'warm').toLowerCase()}">${escapeHtml(c.category || 'Warm')}</span></td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  `;

  // Render Charts using Chart.js
  renderCharts(clients, interactions, todayStr);
}

function renderCharts(clients, interactions, todayStr) {
  // Destroy existing charts to prevent canvas re-use errors
  if (monthlyClientsChart) monthlyClientsChart.destroy();
  if (monthlyInteractionsChart) monthlyInteractionsChart.destroy();
  if (statusDistributionChart) statusDistributionChart.destroy();
  if (followupCompletionChart) followupCompletionChart.destroy();

  const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
  const textColor = isDark ? '#94a3b8' : '#475569';
  const gridColor = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';

  // 1. Monthly Clients Chart
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const clientCounts = new Array(12).fill(0);
  clients.forEach(c => {
    const d = new Date(c.created_at);
    if (!isNaN(d)) clientCounts[d.getMonth()]++;
  });

  const ctxClients = document.getElementById('chart-monthly-clients')?.getContext('2d');
  if (ctxClients) {
    monthlyClientsChart = new Chart(ctxClients, {
      type: 'bar',
      data: {
        labels: months,
        datasets: [{
          label: 'New Clients',
          data: clientCounts,
          backgroundColor: '#4f46e5',
          borderRadius: 6
        }]
      },
      options: {
        responsive: true,
        plugins: { legend: { display: false } },
        scales: {
          x: { ticks: { color: textColor }, grid: { display: false } },
          y: { ticks: { color: textColor }, grid: { color: gridColor }, beginAtZero: true }
        }
      }
    });
  }

  // 2. Monthly Interactions Chart
  const interactionCounts = new Array(12).fill(0);
  interactions.forEach(i => {
    const d = new Date(i.interaction_date);
    if (!isNaN(d)) interactionCounts[d.getMonth()]++;
  });

  const ctxInteractions = document.getElementById('chart-monthly-interactions')?.getContext('2d');
  if (ctxInteractions) {
    monthlyInteractionsChart = new Chart(ctxInteractions, {
      type: 'line',
      data: {
        labels: months,
        datasets: [{
          label: 'Interactions',
          data: interactionCounts,
          borderColor: '#10b981',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          fill: true,
          tension: 0.3
        }]
      },
      options: {
        responsive: true,
        plugins: { legend: { display: false } },
        scales: {
          x: { ticks: { color: textColor }, grid: { display: false } },
          y: { ticks: { color: textColor }, grid: { color: gridColor }, beginAtZero: true }
        }
      }
    });
  }

  // 3. Status Distribution Chart
  const statusCounts = {};
  interactions.forEach(i => {
    statusCounts[i.status] = (statusCounts[i.status] || 0) + 1;
  });

  const ctxStatus = document.getElementById('chart-status-dist')?.getContext('2d');
  if (ctxStatus) {
    statusDistributionChart = new Chart(ctxStatus, {
      type: 'doughnut',
      data: {
        labels: Object.keys(statusCounts),
        datasets: [{
          data: Object.values(statusCounts),
          backgroundColor: ['#6366f1', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899']
        }]
      },
      options: {
        responsive: true,
        plugins: { legend: { position: 'bottom', labels: { color: textColor } } }
      }
    });
  }

  // 4. Follow-up Completion Rate Chart
  const completed = interactions.filter(i => i.status === 'Won' || i.status === 'Closed' || (i.next_followup_date && i.next_followup_date >= todayStr)).length;
  const pendingOverdue = interactions.filter(i => i.next_followup_date && i.next_followup_date < todayStr && i.status !== 'Won' && i.status !== 'Closed').length;

  const ctxCompletion = document.getElementById('chart-followup-completion')?.getContext('2d');
  if (ctxCompletion) {
    followupCompletionChart = new Chart(ctxCompletion, {
      type: 'pie',
      data: {
        labels: ['On Track / Completed', 'Overdue Pending'],
        datasets: [{
          data: [completed, pendingOverdue],
          backgroundColor: ['#10b981', '#ef4444']
        }]
      },
      options: {
        responsive: true,
        plugins: { legend: { position: 'bottom', labels: { color: textColor } } }
      }
    });
  }
}
